/**
 * RevisionsInboxWidget — shows all submissions in `revise` status the
 * caller can see, with their pin-annotation notes inline.
 *
 * Solves today's reported gap (May 18, 2026): CG Supervisor added
 * timecode pin notes on an animation submission; the vendor producer
 * (Mukesh) had no surface in his dashboard to read them. This widget
 * surfaces them on EVERY downstream working-team dashboard.
 *
 * Visible to: vendor producers, vendor producer managers, employees,
 * freelancers, managers, admins. Each user sees the rows scoped to
 * their stake (submitter / vendor company / project access).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  AlertTriangle, MessageSquare, MapPin, ExternalLink, RefreshCw,
  Clock, ChevronDown, ChevronRight, User,
} from 'lucide-react';

function fmtTimecode(sec) {
  if (sec == null) return '';
  const s = Math.floor(sec % 60);
  const m = Math.floor(sec / 60);
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function fmtAgo(iso) {
  if (!iso) return '';
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60)    return `${s}s ago`;
  if (s < 3600)  return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function RevisionsInboxWidget({ compact = false }) {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});  // {submission_id: true}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/reviews/revisions-for-me?limit=50');
      setItems(r.data?.items || []);
    } catch (e) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const toggle = (id) => setExpanded(e => ({ ...e, [id]: !e[id] }));
  const openReview = (sub) => navigate(`/reviews?sub=${sub.id}`);

  if (loading) {
    return (
      <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-5" data-testid="revisions-inbox">
        <div className="flex items-center gap-2 text-zinc-400 text-[13px]">
          <RefreshCw className="w-4 h-4 animate-spin" /> Loading revisions…
        </div>
      </div>
    );
  }

  if (!items.length) {
    return (
      <div className="rounded-xl border border-zinc-800 bg-zinc-900/30 p-5" data-testid="revisions-inbox">
        <div className="flex items-center gap-2 mb-2">
          <AlertTriangle className="w-4 h-4 text-emerald-400" />
          <h3 className="text-sm font-semibold text-zinc-200">Revisions to address</h3>
        </div>
        <p className="text-[12px] text-zinc-500">No pending revisions — your team is all caught up. 🎉</p>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-5" data-testid="revisions-inbox">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-400" />
          <h3 className="text-sm font-semibold text-zinc-200">
            Revisions to address
            <span className="ml-2 text-[11px] font-mono text-amber-300 bg-amber-500/15 px-2 py-0.5 rounded">{items.length}</span>
          </h3>
        </div>
        <button
          onClick={load}
          data-testid="revisions-refresh"
          className="text-[11px] inline-flex items-center gap-1 text-zinc-400 hover:text-zinc-200"
        >
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </div>

      <div className="space-y-2">
        {items.map((sub) => {
          const isOpen = !!expanded[sub.id];
          const pinCount = sub.open_annotations ?? sub.annotation_count ?? 0;
          return (
            <div key={sub.id} className="rounded-lg border border-zinc-800 bg-zinc-950/60" data-testid={`revision-row-${sub.id}`}>
              <button
                type="button"
                onClick={() => toggle(sub.id)}
                className="w-full px-3 py-2 flex items-center gap-3 hover:bg-zinc-900/40 text-left"
              >
                {isOpen ? <ChevronDown className="w-4 h-4 text-zinc-500" /> : <ChevronRight className="w-4 h-4 text-zinc-500" />}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 text-[13px]">
                    <span className="text-zinc-200 font-medium truncate">{sub.title || sub.submission_code || sub.id.slice(0, 8)}</span>
                    {sub.stage && <span className="text-[10px] font-mono text-amber-300 bg-amber-500/15 px-1.5 py-0.5 rounded uppercase">{sub.stage}</span>}
                  </div>
                  <div className="text-[11px] text-zinc-500 flex items-center gap-3">
                    {sub.project_name && <span>{sub.project_code ? `${sub.project_code} · ` : ''}{sub.project_name}</span>}
                    {sub.shot_code && <span>· {sub.shot_code}</span>}
                    <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{fmtAgo(sub.decided_at || sub.submitted_at)}</span>
                  </div>
                </div>
                {pinCount > 0 && (
                  <span className="inline-flex items-center gap-1 text-[11px] font-mono text-rose-300 bg-rose-500/15 px-2 py-0.5 rounded">
                    <MapPin className="w-3 h-3" /> {pinCount}
                  </span>
                )}
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => { e.stopPropagation(); openReview(sub); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); openReview(sub); } }}
                  data-testid={`open-review-${sub.id}`}
                  className="text-[11px] text-sky-300 hover:text-sky-200 inline-flex items-center gap-1 cursor-pointer"
                >
                  Open <ExternalLink className="w-3 h-3" />
                </span>
              </button>

              {isOpen && (
                <div className="px-4 pb-3 pt-1 space-y-2 border-t border-zinc-800">
                  {sub.decided_by_note && (
                    <div className="rounded-md bg-zinc-900/80 border border-zinc-800 px-3 py-2">
                      <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Reviewer summary</p>
                      <p className="text-[12px] text-zinc-200 whitespace-pre-wrap">{sub.decided_by_note}</p>
                    </div>
                  )}
                  {sub.annotations && sub.annotations.length > 0 ? (
                    <div className="space-y-1.5">
                      <p className="text-[10px] uppercase tracking-wider text-zinc-500">Pin notes ({sub.annotations.length})</p>
                      {sub.annotations.map((a, i) => (
                        <div key={a.id || i} className="rounded-md bg-zinc-900/60 border border-zinc-800 px-3 py-2" data-testid={`pin-note-${a.id || i}`}>
                          <div className="flex items-start gap-2">
                            <span className="font-mono text-[10px] text-emerald-300 bg-emerald-500/10 px-1.5 py-0.5 rounded shrink-0">
                              {a.timecode_sec != null ? fmtTimecode(a.timecode_sec) : `#${i + 1}`}
                            </span>
                            <div className="flex-1 min-w-0">
                              <p className="text-[12px] text-zinc-200 whitespace-pre-wrap break-words">{a.text || '(no text — visual markup only)'}</p>
                              <p className="text-[10px] text-zinc-500 mt-1 inline-flex items-center gap-1">
                                <User className="w-3 h-3" />{a.author_name || a.author_handle || 'reviewer'}
                                <span>· {fmtAgo(a.created_at)}</span>
                                {a.resolved && <span className="ml-1 text-emerald-400">✓ resolved</span>}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-[11px] text-zinc-500 italic px-3">
                      <MessageSquare className="w-3 h-3 inline mr-1" />
                      Reviewer marked this for revisions but didn't leave specific pin notes. Click <strong>Open</strong> to see any visual markup on the video.
                    </p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
